/**
 * noga.ts - resolve a NOGA-2025 code to its German Branche label, fully offline.
 *
 * Used by the firma enrichment worker (P2-7) to derive `firma.branche` from the promoted
 * `firma.noga_code` (§3.2: "Branche | A + M-override | NOGA_2025_LABELS[noga_code] (static
 * catalog). Null when no NOGA."), and by the customer Stammdaten card (P2-8) to render it.
 * Backed by the bundled `noga-2025.data.ts` table - no network, no external tool call at
 * runtime (see that file's header for the two-tier design + how to extend it).
 *
 * Matching: normalises the input to digits-only (tolerates dotted BFS notation like
 * "62.01.0" and stray whitespace), tries the exact 5-digit CLASS first, then falls back to
 * the 2-digit DIVISION prefix. A code whose 2-digit prefix isn't a real NOGA-2025 division
 * (or empty/nullish/non-numeric input) resolves to `null` - never a guess.
 *
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §3.1, §3.2, P2-6]
 * [Source: packages/shared/src/noga-2025.data.ts]
 * [Source: apps/api/src/modules/mandate/detect-gender.ts] (offline-resolver-over-static-table precedent)
 */

import { NOGA_2025_LABELS, NOGA_DIVISION_LABELS } from './noga-2025.data';

export { NOGA_2025_LABELS, NOGA_DIVISION_LABELS };

/** Strip everything but digits, e.g. "62.01.0" / " 62010 " -> "62010". */
function toDigits(code: string): string {
  return code.replace(/[^0-9]/g, '');
}

/**
 * Best-effort German Branche label for a NOGA-2025 code. Returns `null` for nullish/empty/
 * non-numeric input, or a code whose 2-digit division prefix is not a real NOGA-2025 division
 * (see `NOGA_DIVISION_LABELS` for the exhaustive list of valid prefixes).
 *
 * Deterministic and pure - the enrichment worker's promoted value and the Stammdaten card's
 * displayed value can never disagree.
 */
export function resolveBranche(nogaCode: string | null | undefined): string | null {
  if (!nogaCode) return null;
  const digits = toDigits(nogaCode);
  if (digits.length < 2) return null;

  // Tier 1: exact 5-digit class match (the curated, higher-precision subset).
  if (digits.length >= 5) {
    const classCode = digits.slice(0, 5);
    const exact = NOGA_2025_LABELS[classCode];
    if (exact) return exact;
  }

  // Tier 2: 2-digit division fallback (the full, verified table) - covers every real code.
  const divisionCode = digits.slice(0, 2);
  return NOGA_DIVISION_LABELS[divisionCode] ?? null;
}
